import React, { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';

interface RouteProgressIndicatorProps {
  className?: string;
}

/**
 * Enterprise-grade top progress indicator for route transitions
 * Respects prefers-reduced-motion
 */
export const RouteProgressIndicator: React.FC<RouteProgressIndicatorProps> = ({ className }) => {
  const location = useLocation();
  const [progress, setProgress] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const animationRef = useRef<number | null>(null);
  const previousPathRef = useRef(location.pathname);
  const startTimeRef = useRef<number>(0);

  // Check for reduced motion preference
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);

    const handler = (e: MediaQueryListEvent) => setPrefersReducedMotion(e.matches);
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    // Skip on same route
    if (previousPathRef.current === location.pathname) {
      return;
    }
    previousPathRef.current = location.pathname;

    // For reduced motion, just flash briefly
    if (prefersReducedMotion) {
      setIsVisible(true);
      setProgress(100);
      const timer = setTimeout(() => {
        setIsVisible(false);
        setProgress(0);
      }, 150);
      return () => clearTimeout(timer);
    }

    // Start progress animation
    setIsVisible(true);
    setProgress(0);
    startTimeRef.current = performance.now();

    const animate = () => {
      const elapsed = performance.now() - startTimeRef.current;
      // Fast initial progress, slows down as it approaches 90%
      const targetProgress = Math.min(90, (elapsed / 300) * 100);
      
      setProgress(prev => {
        const diff = targetProgress - prev;
        return prev + diff * 0.15;
      });

      if (targetProgress < 90) {
        animationRef.current = requestAnimationFrame(animate);
      }
    };

    animationRef.current = requestAnimationFrame(animate);

    // Complete the progress after route settles
    const completeTimer = setTimeout(() => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      setProgress(100);
      
      // Hide after completion
      setTimeout(() => {
        setIsVisible(false);
        setProgress(0);
      }, 200);
    }, 150);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      clearTimeout(completeTimer);
    };
  }, [location.pathname, prefersReducedMotion]);

  if (!isVisible) return null;

  return (
    <div
      className={cn(
        'fixed top-0 left-0 right-0 z-[9999] h-[2px] pointer-events-none',
        className
      )}
      role="progressbar"
      aria-valuenow={Math.round(progress)}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label="Page loading"
    >
      <div
        className={cn(
          'h-full bg-primary shadow-[0_0_10px_hsl(var(--primary)/0.5)]',
          !prefersReducedMotion && 'transition-[width] duration-100 ease-out'
        )}
        style={{ width: `${progress}%` }}
      />
    </div>
  );
};
